import { useContext, useEffect } from "react";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import AuthContext from "@/react/context/authContext.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import AlertNotificationBanner from "@/react/components/alertNotificationBanner/AlertNotificationBanner.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";
import { LeadsProvider, useLeadsContext } from "./context/LeadsContext";
import { LeadDetailPanel } from "./components/LeadDetailPanel";
import { LeadCard } from "./components/LeadCard";
import { LeadsToolbar } from "./components/LeadsToolbar";
import { LeadsEmptyState } from "./components/LeadsEmptyState";
import { useFilteredLeads } from "./hooks/useFilteredLeads";
import { WidgetParamKeys } from "@/types/entity";
import { cn } from "@/modern/utils";

function LeadsViewContent() {
  const { user } = useContext(AuthContext);
  const { editorHandler } = useContext(BryntumContext);
  const { project, entityManager } = useSwivuBryntumContext();
  const { selectedSubmission, selectSubmission, clearFilters } = useLeadsContext();
  const { submissions, filteredSubmissions, hasActiveFilters } = useFilteredLeads();

  const isStoreEmpty = submissions.length === 0;

  useEffect(() => {
    entityManager?.callParamAction(WidgetParamKeys.LEADS.EDIT, (id) => {
      if (!submissions.length) return false;
      const submission = submissions.find((s) => Number(s.id) === Number(id));
      if (submission) {
        selectSubmission(submission);
      }
      return true;
    });
  }, [entityManager, submissions, selectSubmission]);

  useEffect(() => {
    if (submissions.length === 0) return;
    if (entityManager?.otherParams?.[WidgetParamKeys.LEADS.EDIT]) return;
    const newest = submissions[0];
    if (!newest?.createdAt) return;
    const age = Date.now() - new Date(newest.createdAt).getTime();
    if (age > 30_000) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`lead-row-${newest.id}`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(t);
  }, [submissions, entityManager?.otherParams]);

  return (
    <div className={marketingStackPageClassName}>
      {user && (
        // @ts-expect-error — JS module, partial props
        <AlertNotificationBanner project={project} editorHandler={editorHandler} user={user} />
      )}
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.leads} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <LeadsToolbar />
          <div className="min-h-0 flex-1 overflow-hidden">
        <ResizablePanelGroup
          key={selectedSubmission ? "leads-split" : "leads-full"}
          orientation="horizontal"
          className="h-full min-w-0"
        >
          <ResizablePanel id="leads-list" maxSize={selectedSubmission ? "75%" : "100%"}>
            <div className="@container/leads-list h-full min-w-0 overflow-y-auto">
              <div className="flex min-h-full flex-col">
                {isStoreEmpty ? (
                  <LeadsEmptyState />
                ) : filteredSubmissions.length === 0 ? (
                  <div className="m-4 flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 p-12 text-center">
                    <p className="text-sm font-medium text-foreground">No leads match</p>
                    <p className="text-xs text-muted-foreground">
                      Try a different search or adjust filters above.
                    </p>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-4 p-4",
                      "@[28rem]/leads-list:grid-cols-2",
                      "@[42rem]/leads-list:grid-cols-3",
                      !selectedSubmission && "@[56rem]/leads-list:grid-cols-4",
                    )}
                  >
                    {filteredSubmissions.map((s) => (
                      <LeadCard key={s.id} submission={s} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          {selectedSubmission && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel id="lead-detail" defaultSize="35%" minSize="28%" maxSize="50%">
                <div className="box-border flex h-full flex-col pt-4 pr-3 pb-4">
                  <div className="ml-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border/80 bg-card shadow-md">
                    <LeadDetailPanel />
                  </div>
                </div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LeadsView() {
  return (
    <LeadsProvider>
      <LeadsViewContent />
    </LeadsProvider>
  );
}

export default LeadsView;
